import { useId } from 'react';
import { DAY_FIELDS, clock, duration, formatHours, parseHours, type DayField, type Interval } from '@stitch/pbx-adapter/describe';
import { Icon } from '../lib/icons';
import { displayZone, minutesApart, timeIn, zoneName } from '../lib/time';

const DAY_NAMES: Record<DayField, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday'
};

/** Every 15 minutes. */
const STEPS = Array.from({ length: 97 }, (_, i) => i * 15);
const WEEKDAYS: DayField[] = ['mon', 'tue', 'wed', 'thu', 'fri'];
const h = (from: number, to: number) => formatHours([{ from: from * 60, to: to * 60 }]);

const PRESETS: { label: string; days: Partial<Record<DayField, string>> }[] = [
  { label: 'Mon–Fri 9–5', days: { mon: h(9, 17), tue: h(9, 17), wed: h(9, 17), thu: h(9, 17), fri: h(9, 17), sat: '', sun: '' } },
  { label: 'Mon–Fri 8–6', days: { mon: h(8, 18), tue: h(8, 18), wed: h(8, 18), thu: h(8, 18), fri: h(8, 18), sat: '', sun: '' } },
  { label: 'Every day 9–5', days: Object.fromEntries(DAY_FIELDS.map((d) => [d, h(9, 17)])) },
  { label: 'Open 24/7', days: Object.fromEntries(DAY_FIELDS.map((d) => [d, h(0, 24)])) }
];

/**
 * Open/closed per day with time pickers, plus one-click presets. Writes the PBX's own format
 * ("9:00A-5:00P"); a day it can't read is shown as text, untouched, so nothing is lost.
 */
export function HoursEditor({
  values,
  set,
  meaning,
  onMeaning,
  zone
}: {
  values: Record<string, string>;
  set: (field: string, value: string) => void;
  meaning: string;
  onMeaning: (v: string) => void;
  /** The rule's time zone (its own, else the company's): the hours are in it wherever people are. */
  zone?: string;
}) {
  const name = useId();
  return (
    <div className="hours-editor">
      {zone && (
        <p className="muted hours-zone">
          <Icon name="clock" size={15} /> These hours are in {zoneName(zone)}, wherever you are{minutesApart(zone, displayZone()) !== 0 ? ` (it’s ${timeIn(zone)} there now)` : ''}.
        </p>
      )}
      <div className="radio-cards" role="radiogroup" aria-label="What the hours mean">
        <label className="radio-card">
          <input type="radio" name={name} checked={meaning !== 'on'} onChange={() => onMeaning('off')} />
          <span>
            <strong>These are open hours</strong>
            <span className="muted"> The rule switches on outside them, for after-hours routing.</span>
          </span>
        </label>
        <label className="radio-card">
          <input type="radio" name={name} checked={meaning === 'on'} onChange={() => onMeaning('on')} />
          <span>
            <strong>The rule is on during these hours</strong>
            <span className="muted"> For example, a lunch break or a night shift.</span>
          </span>
        </label>
      </div>

      <div className="preset-row" role="group" aria-label="Quick hours">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            className="preset"
            onClick={() => {
              for (const [d, v] of Object.entries(p.days)) set(d, v ?? '');
            }}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          className="preset"
          disabled={!values.mon?.trim()}
          onClick={() => {
            for (const d of WEEKDAYS.slice(1)) set(d, values.mon ?? '');
          }}
        >
          Copy Monday to Tue–Fri
        </button>
      </div>

      <div className="day-rows">
        {DAY_FIELDS.map((d) => (
          <DayRow key={d} day={d} value={values[d] ?? ''} onChange={(v) => set(d, v)} />
        ))}
      </div>
    </div>
  );
}

function DayRow({ day, value, onChange }: { day: DayField; value: string; onChange: (v: string) => void }) {
  const intervals = parseHours(value);
  const label = DAY_NAMES[day];
  if (intervals === null) {
    // Written some other way (in the phone system's own admin): keep it as text.
    return (
      <div className="day-row">
        <span className="day-name">{label}</span>
        <input className="input mono" aria-label={`${label} hours`} value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  const open = intervals.length > 0;
  return (
    <div className={`day-row${open ? '' : ' closed'}`}>
      <span className="day-name">{label}</span>
      <label className="switch">
        <input type="checkbox" role="switch" checked={open} aria-label={`${label} open`} onChange={(e) => onChange(e.target.checked ? formatHours([{ from: 540, to: 1020 }]) : '')} />
        <span>{open ? 'Open' : 'Closed'}</span>
      </label>
      {open ? <Ranges label={label} value={value} onChange={onChange} /> : <span />}
    </div>
  );
}

/**
 * One day's time ranges as 15-minute pickers, with "+ Add" for split hours (up to three).
 * The value is the PBX's own format ("9:00A-12:00P 1:00P-5:00P"); an unreadable value is left alone.
 */
export function Ranges({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const intervals = parseHours(value) ?? [];
  const update = (next: Interval[]) => onChange(formatHours(next));
  return (
    <div className="day-ranges">
      {intervals.map((iv, i) => (
        <span className="range" key={i}>
          <select className="input" aria-label={`${label} opens${i ? ` (${i + 1})` : ''}`} value={iv.from} onChange={(e) => update(intervals.map((x, j) => (j === i ? { ...x, from: Number(e.target.value) } : x)))}>
            {STEPS.filter((m) => m < 1440).map((m) => (
              <option key={m} value={m}>
                {clock(m)}
              </option>
            ))}
          </select>
          <span className="muted">to</span>
          <select className="input" aria-label={`${label} closes${i ? ` (${i + 1})` : ''}`} value={iv.to} onChange={(e) => update(intervals.map((x, j) => (j === i ? { ...x, to: Number(e.target.value) } : x)))}>
            {STEPS.filter((m) => m > 0).map((m) => (
              <option key={m} value={m} disabled={m <= iv.from}>
                {clock(m)}
              </option>
            ))}
          </select>
          {i > 0 && (
            <button type="button" className="link-btn" onClick={() => update(intervals.filter((_, j) => j !== i))}>
              Remove
            </button>
          )}
        </span>
      ))}
      {intervals.length > 0 && intervals.length < 3 && (
        <button
          type="button"
          className="link-btn"
          aria-label={`Add more hours on ${label}`}
          title="Add more hours, for example after a lunch break"
          onClick={() => {
            const last = intervals[intervals.length - 1]!;
            const from = Math.min(last.to + 60, 1380);
            update([...intervals, { from, to: Math.min(from + 120, 1440) }]);
          }}
        >
          + Add
        </button>
      )}
    </div>
  );
}

/** Common durations as a dropdown, so nobody has to think in seconds. */
const DURATIONS: Record<string, number[]> = {
  ring: [10, 15, 20, 25, 30, 45, 60],
  keyWait: [5, 10, 15, 20, 30],
  maxWait: [60, 120, 180, 300, 600, 900, 1200, 1800],
  reminder: [30, 60, 90, 120, 180, 300, 600],
  wrapUp: [0, 10, 15, 20, 30, 45, 60, 90, 120, 180],
  hangup: [30, 60, 120, 180, 300]
};

export function durationPreset(field: string): number[] {
  if (/^stage\dSeconds$/.test(field)) return DURATIONS.ring!;
  if (field === 'timeoutSeconds') return DURATIONS.keyWait!;
  if (field === 'maxWaitSeconds') return DURATIONS.maxWait!;
  if (field === 'reminderSeconds') return DURATIONS.reminder!;
  if (field === 'wrapUpSeconds') return DURATIONS.wrapUp!;
  if (field === 'hangupSeconds') return DURATIONS.hangup!;
  return DURATIONS.ring!;
}

export function durationOptions(field: string, current: string, emptyLabel: string): { value: string; label: string }[] {
  const presets = durationPreset(field).map(String);
  const all = current && !presets.includes(current) ? [...presets, current].sort((a, b) => Number(a) - Number(b)) : presets;
  return [{ value: '', label: emptyLabel }, ...all.map((v) => ({ value: v, label: v === '0' ? 'None' : duration(v) }))];
}
